// Pure Filter logic, ported from js/renderers-filter.js. The Filter widget narrows the
// whole population by stacking bio-attribute chips (Position/League/Club/Nationality/Foot +
// absolute Age/Height/Weight) with any number of per-metric range sliders, for one season on
// one value mode (Per 90 / Total). Reuses the StatsFilterKit engine (matchesFilters +
// metricValueForMode); kept pure (population + categories passed in) so it's testable and
// unchanged at the Track-2 cutover. The slider UI itself is 04g-4c — this layer is just the
// math it and useFilterResults sit on.

import type { ProtoMetricCategory, ProtoPlayerMetric, ProtoFilterResult } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";
import { playerIdOf } from "./players.js";
import { getPlayerShortName, comparePlayersByName } from "./player-utils.js";
import { seasonSortKey } from "./league.js";
import {
  matchesFilters,
  metricValueForMode,
  type ActiveFilters,
  type KitPlayer,
  type KitMetric,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";

type Players = Record<string, RawPlayerRecord>;

/** A dragged slider's current bounds; sparse — a metric key is present only once its slider
 *  has been moved off the full domain (renderers-filter.js:341). */
export interface FilterMetricRange {
  min: number;
  max: number;
}
export type FilterMetricRanges = Record<string, FilterMetricRange>;

/**
 * Every distinct season code with real recorded stats anywhere in the population,
 * newest-first — a Filter spans everyone, not one player's seasons
 * (renderers-filter.js:36). Same computation as Rankings' season list; kept as its own copy,
 * faithful to the prototype's per-file duplication of this trivial helper.
 */
export function filterSeasonCodes(players: Players): string[] {
  const codes = new Set<string>();
  for (const pl of Object.values(players)) {
    const seasons = pl.seasons ?? {};
    for (const code of Object.keys(seasons)) {
      if (Object.keys(seasons[code]?.stats ?? {}).length > 0) codes.add(code);
    }
  }
  return Array.from(codes).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
}

/** Every metric keyed by its own catalog key (globally unique across all 5 categories, so
 *  no categoryId half is needed) — renderers-filter.js:95. */
export function buildMetricByKey(categories: readonly ProtoMetricCategory[]): Map<string, ProtoPlayerMetric> {
  const map = new Map<string, ProtoPlayerMetric>();
  for (const cat of categories) {
    for (const sub of cat.subcategories) {
      for (const m of sub.playerMetrics) map.set(m.key, m);
    }
  }
  return map;
}

/**
 * A metric's 0-to-population-max domain for one season on one value mode. Deliberately
 * 0-anchored (per direct instruction "a slider from 0 to the highest number"), clamped so a
 * degenerate all-negative metric degrades to an empty (max 0) domain rather than inverting
 * (renderers-filter.js:113).
 */
export function metricDomain(
  players: Players,
  metric: ProtoPlayerMetric,
  seasonCode: string,
  mode: string,
): FilterMetricRange {
  let max = 0;
  let any = false;
  for (const pl of Object.values(players)) {
    const stats = pl.seasons?.[seasonCode]?.stats ?? {};
    const v = metricValueForMode(stats, metric as KitMetric, mode);
    if (typeof v === "number" && !Number.isNaN(v)) {
      any = true;
      if (v > max) max = v;
    }
  }
  return { min: 0, max: any ? max : 0 };
}

// --- slider math (pure; the 04g-4c imperative island's geometry) --------------
// Extracted from renderers-filter.js's wireSliderRow so the value<->pixel<->percent
// conversions are testable independently of the drag DOM (the island itself is untested,
// like the chip-bar). Deliberately NOT rounded to whole numbers — several real metric
// domains are under 1 (e.g. xG per 90), which rounding would collapse (renderers-filter.js:153).

/** A range value's position along its track, as a 0–100 percent. */
export function valueToPct(value: number, domain: FilterMetricRange): number {
  const span = domain.max - domain.min;
  return span > 0 ? ((value - domain.min) / span) * 100 : 0;
}

/** A pixel offset within the track back to a domain value (clamped to the track). */
export function pxToValue(px: number, trackWidth: number, domain: FilterMetricRange): number {
  const span = domain.max - domain.min;
  const frac = trackWidth > 0 ? Math.max(0, Math.min(1, px / trackWidth)) : 0;
  return domain.min + frac * span;
}

/** A range is "default" (applies no constraint) when it spans the whole domain. */
export function isDefaultRange(range: FilterMetricRange, domain: FilterMetricRange): boolean {
  return range.min <= domain.min && range.max >= domain.max;
}

/** Move one handle, keeping min ≤ max (dragging a handle past the other pins them together) —
 *  renderers-filter.js:197. */
export function applyHandle(
  range: FilterMetricRange,
  handle: "min" | "max",
  value: number,
): FilterMetricRange {
  return handle === "min"
    ? { min: Math.min(value, range.max), max: range.max }
    : { min: range.min, max: Math.max(value, range.min) };
}

/**
 * A player passes every dragged slider. A range applies via the metric's own value mode
 * (metricValueForMode, matching the scale its domain was built on); a metric with no total
 * mode reads its single real value regardless. A missing/NaN value fails the range — an
 * unrecorded metric can't satisfy a lower bound (renderers-filter.js:131).
 */
export function matchesMetricRanges(
  pl: RawPlayerRecord,
  metricRanges: FilterMetricRanges,
  seasonCode: string,
  mode: string,
  metricMap: Map<string, ProtoPlayerMetric>,
): boolean {
  const stats = pl.seasons?.[seasonCode]?.stats ?? {};
  for (const key of Object.keys(metricRanges)) {
    const range = metricRanges[key]!;
    const metric = metricMap.get(key);
    const val = metric ? metricValueForMode(stats, metric as KitMetric, mode) : stats[key];
    if (typeof val !== "number" || Number.isNaN(val)) return false;
    if (val < range.min || val > range.max) return false;
  }
  return true;
}

/**
 * The matched population: every player passing both the bio filters AND every metric range,
 * sorted by name (surname) — the renderer only paginates (renderers-filter.js:143 + :507).
 */
export function computeFilterMatches(
  players: Players,
  activeBio: ActiveFilters,
  refPlayer: KitPlayer,
  seasonCode: string,
  metricRanges: FilterMetricRanges,
  mode: string,
  metricMap: Map<string, ProtoPlayerMetric>,
  bioRangeFilters: RangeFilterConfig,
): ProtoFilterResult[] {
  const matched: RawPlayerRecord[] = [];
  for (const key of Object.keys(players)) {
    const pl = players[key];
    if (!pl) continue;
    if (!matchesFilters(pl as unknown as KitPlayer, activeBio, refPlayer, bioRangeFilters)) continue;
    if (!matchesMetricRanges(pl, metricRanges, seasonCode, mode, metricMap)) continue;
    matched.push(pl);
  }
  matched.sort(comparePlayersByName);
  return matched.map((pl) => ({
    playerId: playerIdOf(pl),
    name: pl.name ?? "",
    shortName: getPlayerShortName(pl),
    team: pl.team ?? null,
    competition: pl.competition ?? null,
  }));
}
